import React from 'react';
import { Option } from 'effect';
import type { ICellRendererParams } from 'ag-grid-community';

import Box from '@mui/material/Box';
import { SYMBOL_FOR_EMPTY_CELL } from '@/components/AgGrid/helpers';
import parseAmount from '@/pages/company/BankFeedRoute/components/BankFeedGrid/BankFeedSumStatusPanel/parseAmount';

// credit/debit are `string | null` per the bank-feed Zod schema (BankFeedTransactionRow).
type AmountCellProps = ICellRendererParams<unknown, string | null>;

// The Credit Card tab delivers a single signed `amount` column instead of the Bank tab's
// split credit/debit pair, so its colour is driven by the value's sign rather than the field
// name: positive → success, negative → error, zero / unparseable → neutral.
const signColor = (value: string | null): string =>
  Option.match(parseAmount(value), {
    onNone: () => 'text.primary',
    onSome: (n) => (n > 0 ? 'success.main' : n < 0 ? 'error.main' : 'text.primary'),
  });

const colorFor = (field: string | undefined, value: string | null): string => {
  if (field === 'credit') return 'success.main';
  if (field === 'debit') return 'error.main';
  if (field === 'amount') return signColor(value);
  return 'text.primary';
};

const AmountCell: React.FC<AmountCellProps> = ({ value, valueFormatted, colDef }) => (
  <Box
    component="span"
    // Amounts are signed numeric strings (e.g. `-845.23`). In the Hebrew (RTL) layout the grid
    // sets `dir="rtl"`, and the leading minus (a weak bidi char) adopts the paragraph direction —
    // rendering on the wrong side of the number. `dir="ltr"` isolates the value as an LTR run
    // (== `direction: ltr; unicode-bidi: isolate`). (DA-19120)
    dir="ltr"
    // `text-align` lives in inline `style`, not `sx`: the RTL emotion cache (stylis-plugin-rtl,
    // LabeledThemeProvider) flips `right`→`left` at compile time, which left-aligns amounts in
    // Hebrew. Inline style bypasses stylis and stays physically right in both layouts. (DA-19120)
    style={{ textAlign: 'right' }}
    sx={{
      flex: 1,
      fontVariantNumeric: 'tabular-nums',
      color: value?.trim() ? colorFor(colDef?.field, value) : 'text.secondary',
    }}
  >
    {valueFormatted ?? SYMBOL_FOR_EMPTY_CELL}
  </Box>
);

export default AmountCell;
